import { db, collection, query, where, getDocs, doc, getDoc, updateDoc } from "@/firebase";
import { logDebug, logInfo, logWarn, logError } from "@/utils/secureLogger";

export interface UserRatingStatus {
  averageRating: number;
  totalRatings: number;
  isUnderReview: boolean;
  needsReview: boolean; // Rating ≤ 2.5 (suspension)
  needsWarning: boolean; // Rating 2.6-2.8 (warning only)
  lastChecked: Date;
}

/**
 * Calculate average rating for a user - PRE-CALCULATED VERSION (INSTANT!)
 * ✅ PERFORMANCE FIX: Reads from user document instead of querying all feedbacks
 * ✅ Rating is updated automatically when feedback is submitted (see Cloud Function)
 * ✅ Two-tier system - Warning (2.6-2.8) + Suspension (≤2.5)
 * @param userId - The user ID to get rating for
 * @returns UserRatingStatus object with rating info
 */
export async function calculateUserAverageRating(userId: string): Promise<UserRatingStatus> {
  try {
    logDebug("Fetching pre-calculated rating for user", { userId });

    // ✅ INSTANT READ: Get rating from user document (no query needed!)
    const userDoc = await getDoc(doc(db, "users", userId));

    if (!userDoc.exists()) {
      logWarn("User document not found", { userId });
      return {
        averageRating: 0,
        totalRatings: 0,
        isUnderReview: false,
        needsReview: false,
        needsWarning: false,
        lastChecked: new Date(),
      };
    }

    const userData = userDoc.data();

    // Read pre-calculated values from user document
    const averageRating = userData?.averageRating || 0;
    const totalRatings = userData?.totalRatings || 0;
    const isUnderReview = userData?.accountUnderReview || false;

    // ✅ Only check if user has AT LEAST 1 rating
    if (totalRatings === 0) {
      logDebug("No ratings found for user (count = 0)", { userId });
      return {
        averageRating: 0,
        totalRatings: 0,
        isUnderReview: false,
        needsReview: false,
        needsWarning: false,
        lastChecked: new Date(),
      };
    }

    // ✅ Two-tier warning system
    // Warning: 2.6 ≤ rating ≤ 2.8
    // Suspension: rating ≤ 2.5
    const needsWarning = totalRatings >= 1 && averageRating >= 2.6 && averageRating <= 2.8;
    const needsReview = totalRatings >= 1 && averageRating <= 2.5;

    if (needsWarning) {
      logWarn("User rating in warning zone (2.6-2.8) - showing warning", {
        userId,
        averageRating: averageRating.toFixed(1),
        totalRatings,
      });
    }

    if (needsReview && !isUnderReview) {
      logWarn("User rating dropped to 2.5 or below - needs review", {
        userId,
        averageRating: averageRating.toFixed(1),
        totalRatings,
      });
    }

    logInfo("User rating fetched from document", {
      userId,
      averageRating: averageRating.toFixed(1),
      totalRatings,
      lastUpdated: userData?.lastRatingUpdate?.toDate?.() || "never",
    });

    return {
      averageRating: parseFloat(averageRating.toFixed(1)),
      totalRatings,
      isUnderReview,
      needsReview,
      needsWarning,
      lastChecked: new Date(),
    };
  } catch (error: any) {
    // ✅ Better error handling
    if (error.code === "permission-denied") {
      logWarn(`Permission denied reading user document for ${userId} - returning safe defaults`);
      return {
        averageRating: 0,
        totalRatings: 0,
        isUnderReview: false,
        needsReview: false,
        needsWarning: false,
        lastChecked: new Date(),
      };
    }
    logError(`Error fetching user rating for ${userId}`, error);
    throw error;
  }
}

/**
 * Flag user account for review due to low ratings
 * @param userId - The user ID to flag
 * @param ratingStatus - The rating status information
 */
export async function flagUserForReview(
  userId: string,
  ratingStatus: UserRatingStatus
): Promise<void> {
  try {
    logInfo("Flagging user account for review", {
      userId,
      averageRating: ratingStatus.averageRating,
      totalRatings: ratingStatus.totalRatings,
    });

    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      accountUnderReview: true,
      reviewReason: "low_rating",
      reviewTriggeredAt: new Date(),
      averageRatingAtReview: ratingStatus.averageRating,
      totalRatingsAtReview: ratingStatus.totalRatings,
      accountStatus: "suspended",
    });

    logInfo("User account flagged for review successfully", { userId });
  } catch (error) {
    logError(`Error flagging user for review: ${userId}`, error);
    throw error;
  }
}

/**
 * Check if user should be shown the review modal
 * @param userId - The user ID to check
 * @returns true if user should see review modal
 */
export async function shouldShowReviewModal(userId: string): Promise<boolean> {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    return userData?.accountUnderReview === true || userData?.accountStatus === "suspended";
  } catch (error) {
    logError(`Error checking review modal status for ${userId}`, error);
    return false;
  }
}

/**
 * Check user rating and flag for review if necessary
 * This is the main function to be called on auth state change
 * @param userId - The user ID to check
 * @returns true if user is under review or needs review
 */
export async function checkAndFlagLowRating(userId: string): Promise<boolean> {
  try {
    // First check if already under review to avoid unnecessary calculations
    const alreadyUnderReview = await shouldShowReviewModal(userId);
    if (alreadyUnderReview) {
      return true;
    }

    // Calculate current rating
    const ratingStatus = await calculateUserAverageRating(userId);

    // If rating is low and user is not already under review, flag them
    if (ratingStatus.needsReview && !ratingStatus.isUnderReview) {
      await flagUserForReview(userId, ratingStatus);
      return true;
    }

    return false;
  } catch (error) {
    logError(`Error in checkAndFlagLowRating for ${userId}`, error);
    return false;
  }
}

/**
 * Clear review status (for admin use)
 * @param userId - The user ID to clear review status for
 */
export async function clearReviewStatus(userId: string): Promise<void> {
  try {
    logInfo("Clearing review status for user", { userId });

    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      accountUnderReview: false,
      reviewReason: null,
      reviewTriggeredAt: null,
      averageRatingAtReview: null,
      totalRatingsAtReview: null,
      accountStatus: "active",
      reviewClearedAt: new Date(),
    });

    logInfo("Review status cleared successfully", { userId });
  } catch (error) {
    logError(`Error clearing review status for ${userId}`, error);
    throw error;
  }
}
